import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  fetchBugById,
  fetchBugComments,
  addBugComment,
  deleteBugComment,
  acknowledgeBug,
  assignBug,
  transitionBugStatus,
  resolveBug,
  reopenBug,
  closeBug,
  clearCurrentBug,
  BugStatus,
} from '../../../store/bugSlice';

const STATUS_COLORS: Record<BugStatus, string> = {
  reported: 'bg-gray-100 text-gray-700',
  acknowledged: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-indigo-100 text-indigo-700',
  testing: 'bg-purple-100 text-purple-700',
  fixed: 'bg-green-100 text-green-700',
  verified: 'bg-teal-100 text-teal-700',
  closed: 'bg-gray-200 text-gray-500',
  wont_fix: 'bg-gray-200 text-gray-500',
  duplicate: 'bg-gray-200 text-gray-500',
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'text-red-700 bg-red-50 border-red-200',
  high: 'text-orange-700 bg-orange-50 border-orange-200',
  medium: 'text-yellow-700 bg-yellow-50 border-yellow-200',
  low: 'text-gray-600 bg-gray-50 border-gray-200',
};

const STATUS_TRANSITIONS: Record<BugStatus, BugStatus[]> = {
  reported: ['acknowledged', 'wont_fix'],
  acknowledged: ['in_progress', 'wont_fix'],
  in_progress: ['testing', 'fixed'],
  testing: ['fixed', 'acknowledged'],
  fixed: ['verified', 'acknowledged'],
  verified: ['closed'],
  closed: ['reported'],
  wont_fix: ['reported'],
  duplicate: [],
};

interface Props {
  bugIdProp?: string;
  onBack?: () => void;
}

const BugDetail: React.FC<Props> = ({ bugIdProp, onBack }) => {
  const params = useParams<{ id: string }>();
  const id = bugIdProp ?? params.id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentBug: bug, comments, loading, commentsLoading } = useAppSelector(s => s.bugs);
  const currentUser = useAppSelector(s => s.auth.user);

  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [tab, setTab] = useState<'details' | 'comments' | 'history'>('details');
  const [reopenReason, setReopenReason] = useState('');
  const [showReopen, setShowReopen] = useState(false);
  const [resolveData, setResolveData] = useState({ version: '', fixDescription: '' });
  const [showResolve, setShowResolve] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchBugById(id));
      dispatch(fetchBugComments(id));
    }
    return () => { dispatch(clearCurrentBug()); };
  }, [dispatch, id]);

  const handleTransition = (status: BugStatus) => {
    if (!id) return;
    if (status === 'fixed') { setShowResolve(true); return; }
    if (status === 'reported') { setShowReopen(true); return; }
    dispatch(transitionBugStatus({ id, status }));
  };

  const handleResolve = () => {
    if (!id) return;
    dispatch(resolveBug({ id, ...resolveData }));
    setShowResolve(false);
  };

  const handleReopen = () => {
    if (!id || !reopenReason.trim()) return;
    dispatch(reopenBug({ id, reason: reopenReason }));
    setShowReopen(false);
    setReopenReason('');
  };

  const handleComment = async () => {
    if (!id || !newComment.trim()) return;
    await dispatch(addBugComment({ bugId: id, content: newComment, isInternal }));
    setNewComment('');
    setIsInternal(false);
  };

  const handleDeleteComment = (commentId: string) => {
    if (!id) return;
    dispatch(deleteBugComment({ bugId: id, commentId }));
  };

  if (loading && !bug) {
    return <div className="flex justify-center p-12"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;
  }
  if (!bug) return <div className="text-center py-12 text-gray-500">Bug not found.</div>;

  const allowedTransitions = STATUS_TRANSITIONS[bug.status] ?? [];
  const isAdmin = currentUser?.role === 'admin';

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to bugs
        </button>
      )}
      {/* Header */}
      <div className={`rounded-xl border p-5 ${SEVERITY_COLORS[bug.impact.severity]}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[bug.status]}`}>
                {bug.status.replace('_', ' ')}
              </span>
              <span className="text-xs font-medium uppercase tracking-wide opacity-70">
                {bug.impact.severity}
              </span>
              {bug.priority !== bug.impact.severity && (
                <span className="text-xs opacity-60">Priority: {bug.priority}</span>
              )}
            </div>
            <h1 className="text-xl font-bold text-gray-900">{bug.title}</h1>
            <p className="text-sm opacity-70 mt-0.5">
              Reported by {bug.reportedBy.name} · {new Date(bug.reportedAt).toLocaleDateString()}
            </p>
          </div>

          {/* Status transitions */}
          <div className="flex gap-2 flex-wrap">
            {bug.status === 'reported' && (
              <button
                onClick={() => dispatch(acknowledgeBug(bug._id))}
                className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-blue-700"
              >
                Acknowledge
              </button>
            )}
            {allowedTransitions.map(next => (
              <button
                key={next}
                onClick={() => handleTransition(next)}
                className="bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded text-sm hover:bg-gray-50 capitalize"
              >
                → {next.replace('_', ' ')}
              </button>
            ))}
            {!['closed', 'wont_fix'].includes(bug.status) && (
              <button
                onClick={() => dispatch(closeBug(bug._id))}
                className="bg-white border border-gray-300 text-gray-600 px-3 py-1.5 rounded text-sm hover:bg-gray-50"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {(['details', 'comments', 'history'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${
              tab === t
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t}
            {t === 'comments' && comments.length > 0 && (
              <span className="ml-1.5 bg-gray-200 text-gray-600 text-xs px-1.5 py-0.5 rounded-full">{comments.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Details tab */}
      {tab === 'details' && (
        <div className="grid grid-cols-3 gap-5">
          <div className="col-span-2 space-y-5">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-2">Description</h2>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{bug.description}</p>
            </div>

            {bug.reproductionSteps && bug.reproductionSteps.steps.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-gray-700 mb-2">Reproduction Steps</h2>
                <ol className="space-y-1">
                  {bug.reproductionSteps.steps.map((step, i) => (
                    <li key={i} className="text-sm text-gray-700 flex gap-2">
                      <span className="text-gray-400 shrink-0">{i + 1}.</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
                {bug.reproductionSteps.environment && (
                  <p className="text-xs text-gray-500 mt-2">Env: {bug.reproductionSteps.environment}</p>
                )}
              </div>
            )}

            {bug.fixedIn && (bug.fixedIn.version || bug.fixedIn.fixDescription) && (
              <div className="bg-green-50 rounded-xl border border-green-200 p-5">
                <h2 className="text-sm font-semibold text-green-800 mb-2">Fix Details</h2>
                {bug.fixedIn.version && <p className="text-sm text-green-700">Version: {bug.fixedIn.version}</p>}
                {bug.fixedIn.fixDescription && <p className="text-sm text-green-700 mt-1">{bug.fixedIn.fixDescription}</p>}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3 text-sm">
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase">Severity</span>
                <p className="mt-0.5 capitalize font-medium text-gray-900">{bug.impact.severity}</p>
              </div>
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase">Priority</span>
                <p className="mt-0.5 capitalize font-medium text-gray-900">{bug.priority}</p>
              </div>
              {bug.impact.affectedUsers !== undefined && (
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase">Affected Users</span>
                  <p className="mt-0.5 font-medium text-gray-900">{bug.impact.affectedUsers}</p>
                </div>
              )}
              {bug.assignedTo.length > 0 && (
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase">Assigned To</span>
                  {bug.assignedTo.map(a => (
                    <p key={a.userId} className="mt-0.5 text-gray-700">{a.name}</p>
                  ))}
                </div>
              )}
              {bug.acknowledgedAt && (
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase">Acknowledged</span>
                  <p className="mt-0.5 text-gray-600">{new Date(bug.acknowledgedAt).toLocaleString()}</p>
                </div>
              )}
              {bug.resolvedAt && (
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase">Resolved</span>
                  <p className="mt-0.5 text-gray-600">{new Date(bug.resolvedAt).toLocaleString()}</p>
                </div>
              )}
              {bug.tags.length > 0 && (
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase">Tags</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {bug.tags.map(tag => (
                      <span key={tag} className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comments tab */}
      {tab === 'comments' && (
        <div className="space-y-4">
          {commentsLoading ? (
            <div className="flex justify-center p-4"><div className="animate-spin h-6 w-6 border-4 border-blue-500 border-t-transparent rounded-full" /></div>
          ) : comments.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-6">No comments yet.</p>
          ) : (
            <div className="space-y-3">
              {comments.map(c => (
                <div key={c._id} className={`bg-white rounded-xl border p-4 ${c.isInternal ? 'border-yellow-200 bg-yellow-50' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="text-sm font-medium text-gray-900">{c.author.name}</span>
                      {c.isInternal && <span className="ml-2 text-xs bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded">Internal</span>}
                      <span className="ml-2 text-xs text-gray-400">{new Date(c.createdAt).toLocaleString()}</span>
                    </div>
                    {(c.author.userId === currentUser?._id || isAdmin) && (
                      <button
                        onClick={() => handleDeleteComment(c._id)}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{c.content}</p>
                  {c.reactions.length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {Object.entries(
                        c.reactions.reduce((acc: Record<string, number>, r) => {
                          acc[r.emoji] = (acc[r.emoji] ?? 0) + 1; return acc;
                        }, {})
                      ).map(([emoji, count]) => (
                        <span key={emoji} className="text-xs bg-gray-100 rounded-full px-2 py-0.5">{emoji} {count}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add comment */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <textarea
              rows={3}
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder="Add a comment…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex items-center justify-between">
              {isAdmin && (
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} />
                  Internal only
                </label>
              )}
              <button
                onClick={handleComment}
                disabled={!newComment.trim()}
                className="ml-auto bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                Comment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History tab */}
      {tab === 'history' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          {bug.reopenHistory.length === 0 && bug.breachHistory.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">No history recorded.</p>
          ) : (
            <div className="space-y-3">
              {bug.reopenHistory.map((r, i) => (
                <div key={i} className="border-l-2 border-yellow-400 pl-3">
                  <p className="text-sm font-medium text-gray-900">Reopened by {r.reopenedByName}</p>
                  <p className="text-xs text-gray-500">{new Date(r.reopenedAt).toLocaleString()} · {r.reason}</p>
                </div>
              ))}
              {bug.breachHistory.map((b, i) => (
                <div key={i} className="border-l-2 border-red-400 pl-3">
                  <p className="text-sm font-medium text-gray-900">SLA Breach recorded</p>
                  <p className="text-xs text-gray-500">{new Date(b.breachedAt).toLocaleString()} · {b.reason}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Resolve modal */}
      {showResolve && (
        <div className="!mt-0 fixed inset-0 bg-black/40 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4 space-y-4">
            <h3 className="font-semibold text-gray-900">Mark as Fixed</h3>
            <input
              placeholder="Version (e.g. 2.1.4)"
              value={resolveData.version}
              onChange={e => setResolveData(d => ({ ...d, version: e.target.value }))}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
            <textarea
              placeholder="Fix description"
              rows={3}
              value={resolveData.fixDescription}
              onChange={e => setResolveData(d => ({ ...d, fixDescription: e.target.value }))}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowResolve(false)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
              <button onClick={handleResolve} className="bg-green-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-green-700">
                Mark Fixed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reopen modal */}
      {showReopen && (
        <div className="!mt-0 fixed inset-0 bg-black/40 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4 space-y-4">
            <h3 className="font-semibold text-gray-900">Reopen Bug</h3>
            <textarea
              placeholder="Reason for reopening *"
              rows={3}
              value={reopenReason}
              onChange={e => setReopenReason(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowReopen(false)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
              <button
                onClick={handleReopen}
                disabled={!reopenReason.trim()}
                className="bg-orange-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
              >
                Reopen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BugDetail;
